import { useRef, useLayoutEffect } from 'react';
import { motion } from 'framer-motion';
import { SparkleStar } from '@peacecover/icon';
import ProfileAvatar from '../../my-page/components/ProfileAvatar';

interface ChatContentItemProps {
  chatType: string;
  text: string;
  pictureUrl?: string | null;
  direction?: 'left' | 'right';
  showStar?: boolean;
  className?: string;
}

const ChatContentItem = ({
  chatType,
  text,
  pictureUrl,
  direction = 'left',
  showStar = false,
  className
}: ChatContentItemProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLParagraphElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: text, chatType 변경 시 DOM 구조/내용이 바뀌므로 재측정 필요
  useLayoutEffect(() => {
    if (!containerRef.current || !textRef.current) return;
    const container = containerRef.current;
    let rafId: number;

    const calculateWidth = () => {
      if (!containerRef.current || !textRef.current) return;
      container.style.width = 'fit-content';

      const range = document.createRange();
      range.selectNodeContents(textRef.current);
      const rects = range.getClientRects();

      let maxLineWidth = 0;
      for (let i = 0; i < rects.length; i++) {
        maxLineWidth = Math.max(maxLineWidth, rects[i].width);
      }

      if (maxLineWidth > 0) {
        const styles = window.getComputedStyle(container);
        const paddingLeft = Number.parseFloat(styles.paddingLeft) || 0;
        const paddingRight = Number.parseFloat(styles.paddingRight) || 0;
        const borderLeft = Number.parseFloat(styles.borderLeftWidth) || 0;
        const borderRight = Number.parseFloat(styles.borderRightWidth) || 0;

        const newWidth = Math.ceil(
          maxLineWidth + paddingLeft + paddingRight + borderLeft + borderRight + 1
        );
        container.style.width = `${newWidth}px`;
      }
    };

    calculateWidth();

    const update = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        calculateWidth();
      });
    };

    let observer: ResizeObserver | null = null;
    if (window.ResizeObserver && container.parentElement) {
      observer = new ResizeObserver(() => {
        update();
      });
      observer.observe(container.parentElement);
    }

    const t1 = setTimeout(update, 100);
    const t2 = setTimeout(update, 400);

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(t1);
      clearTimeout(t2);
      if (observer) observer.disconnect();
    };
  }, [text, chatType]);

  const paddingClassName = direction === 'left' ? 'pl-[12px] pr-[15px]' : 'pr-[12px] pl-[15px]';
  switch (chatType) {
    case 'passage':
      return (
        <div className={`px-[24px] pb-[24px] ${className}`}>
          <div
            className="relative text-[15px] leading-[28px] text-white break-keep"
            style={{
              fontFamily: '"Noto Serif KR", ui-serif, Georgia, "Times New Roman", serif',
              letterSpacing: '3.4%'
            }}
          >
            {text}
            <StarAnimation
              showStar={showStar}
              containerClassName="top-[-10px] left-[-8px]"
              bigStarClassName="top-[13px] left-[-8px]"
              smallStarClassName="top-[7px] left-[2px]"
            />
          </div>
        </div>
      );
    case 'action':
      return (
        <div
          className={`px-[12px] py-[8px] border-x border-[rgba(255,255,255,0.12)] mx-[16px] pb-[24px] flex items-center justify-center ${className}`}
        >
          <div className="relative w-fit text-[13px] leading-[16px] text-main-dark-4 text-center break-keep">
            {text}
            <StarAnimation
              showStar={showStar}
              containerClassName="top-[-17px] left-[-6px]"
              bigStarClassName="top-[13px] left-[-8px]"
              smallStarClassName="top-[7px] left-[2px]"
            />
          </div>
        </div>
      );
    case 'narration':
    case 'system':
      return (
        <div className={`flex flex-col items-center gap-[8px] pb-[24px] ${className}`}>
          <p className="m-0 text-[10px] leading-[18px] text-main-dark-3">✦</p>
          <div
            className="relative text-[13px] leading-[18px] text-[#FFFFFF] text-center px-[16px] py-[8px] break-keep"
            style={{
              fontFamily: '"Noto Serif KR", ui-serif, Georgia, "Times New Roman", serif'
            }}
          >
            {text}
            <StarAnimation
              showStar={showStar}
              containerClassName="top-[-7px] left-[11px]"
              bigStarClassName="top-[13px] left-[-8px]"
              smallStarClassName="top-[7px] left-[2px]"
            />
          </div>
        </div>
      );
    case 'dialogue':
      return (
        <div
          className={`flex ${direction === 'left' ? 'flex-row' : 'flex-row-reverse'} items-end justify-start gap-[4px] ${paddingClassName} pb-[24px] ${className}`}
        >
          <ProfileAvatar
            thumbnailUrl={pictureUrl}
            widthAndHeight="26px"
            svgWidthAndHeight="26px"
            backgroundColor="#504D7F"
          />
          <div
            ref={containerRef}
            className={`relative px-[16px] py-[14px] rounded-[16px] max-w-[273px] ${direction === 'left' ? 'bg-[rgba(255,255,255,0.15)]' : 'bg-transparent border border-[#FFF]'}`}
            style={{ width: 'fit-content' }}
          >
            <p
              ref={textRef}
              className={`m-0 text-[15px] leading-[18px] ${direction === 'left' ? 'text-main-dark-2' : 'text-pure-white'} text-left break-keep`}
            >
              {text}
            </p>
            <StarAnimation
              showStar={showStar}
              containerClassName={`${direction === 'left' ? 'top-[-8px] right-[-8px]' : 'top-[-13px] left-[-5px]'}`}
              bigStarClassName={`top-[13px] ${direction === 'left' ? 'right-[-8px]' : 'left-[-8px]'}`}
              smallStarClassName={`top-[7px] ${direction === 'left' ? 'right-[2px]' : 'left-[2px]'}`}
            />
          </div>
        </div>
      );
    case 'thought':
      return (
        <div
          className={`flex ${direction === 'left' ? 'flex-row' : 'flex-row-reverse'} items-end justify-start gap-[4px] ${paddingClassName} pb-[24px] ${className}`}
        >
          <div className="relative h-fit">
            <svg className="absolute top-0 left-0 right-0 bottom-0" aria-hidden="true">
              <filter id="chunky-dot">
                <feTurbulence type="turbulence" baseFrequency="0.2" numOctaves="3" result="noise" />
                <feDisplacementMap in="SourceGraphic" in2="noise" scale="2" />
              </filter>
            </svg>

            <div className={`absolute top-[-30px] ${direction === 'left' ? 'right-0' : 'left-0'}`}>
              <div className="relative inline-block w-[7px] h-[7px]">
                <div
                  className={`absolute inset-0 ${direction === 'left' ? 'bg-[rgba(255,255,255,0.15)] border-[rgba(255,255,255,0.075)]' : 'bg-transparent border-white'} border rounded-full`}
                  style={{
                    filter: 'url(#chunky-dot)'
                  }}
                />
              </div>
            </div>
            <div
              className={`absolute top-[-24px] ${direction === 'left' ? 'right-[8.5px]' : 'left-[8.5px]'}`}
            >
              <div className="relative inline-block w-[4px] h-[4px]">
                <div
                  className={`absolute inset-0 ${direction === 'left' ? 'bg-[rgba(255,255,255,0.15)] border-[rgba(255,255,255,0.075)]' : 'bg-transparent border-white'} border rounded-full`}
                  style={{
                    filter: 'url(#chunky-dot)',
                    transform: 'scale(1.2)'
                  }}
                />
              </div>
            </div>
            <ProfileAvatar
              thumbnailUrl={pictureUrl}
              widthAndHeight="26px"
              svgWidthAndHeight="26px"
              backgroundColor="#504D7F"
            />
          </div>
          <svg className="absolute top-0 left-0 right-0 bottom-0" aria-hidden="true">
            <filter id="rough-border-only">
              <feTurbulence
                type="turbulence"
                baseFrequency="0.1 0.1"
                numOctaves="2"
                result="noise"
              />
              <feDisplacementMap in="SourceGraphic" in2="noise" scale="3" />
            </filter>
          </svg>

          <div
            ref={containerRef}
            className="relative inline-block px-[16px] py-[14px] max-w-[273px]"
            aria-hidden="true"
            style={{ width: 'fit-content' }}
          >
            <div
              className={`absolute top-0 left-0 right-0 bottom-0 ${direction === 'left' ? 'bg-[rgba(255,255,255,0.15)] border-[rgba(255,255,255,0.075)]' : 'bg-transparent border-white'} border rounded-[20px] z-1`}
              style={{
                filter: 'url(#rough-border-only)'
              }}
            />

            <p
              ref={textRef}
              className={`m-0 relative z-2 ${direction === 'left' ? 'text-main-dark-2' : 'text-pure-white'} text-[15px] leading-[18px] break-keep`}
            >
              {text}
            </p>
            <StarAnimation
              showStar={showStar}
              containerClassName={`${direction === 'left' ? 'top-[-8px] right-[-8px]' : 'top-[-13px] left-[-5px]'}`}
              bigStarClassName={`top-[13px] ${direction === 'left' ? 'right-[-8px]' : 'left-[-8px]'}`}
              smallStarClassName={`top-[7px] ${direction === 'left' ? 'right-[2px]' : 'left-[2px]'}`}
            />
          </div>
        </div>
      );
  }
  return null;
};

export default ChatContentItem;

const StarAnimation = ({
  showStar,
  containerClassName,
  bigStarClassName,
  smallStarClassName
}: {
  showStar: boolean;
  containerClassName: string;
  bigStarClassName: string;
  smallStarClassName: string;
}) => {
  return (
    <motion.div
      className={`absolute pointer-events-none ${containerClassName}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: showStar ? 1 : 0 }}
      transition={{ duration: 0.4, ease: 'easeInOut' }}
      aria-hidden={!showStar}
    >
      <SparkleStar
        size={11}
        className={`absolute ${bigStarClassName}`}
        gradientStops={[
          { offset: '-11%', color: '#45E0AD' },
          { offset: '100%', color: '#5C3DFA' }
        ]}
      />
      <SparkleStar
        size={7}
        className={`absolute ${smallStarClassName}`}
        gradientStops={[
          { offset: '-11%', color: '#45E0AD' },
          { offset: '100%', color: '#5C3DFA' }
        ]}
      />
    </motion.div>
  );
};
